import { FilterCriteria } from 'src/app/model/common/filterCriteria';
import { Audit } from 'src/app/model/common/audit';
import { getCorrectedDate, isNullOrUndefined } from 'src/app/utils/utils';
import { FormArray } from '@angular/forms';

/**
 * Pure function to apply uniqueness to an array.
 */
export function onlyUnique(value: any, index: any, self: any[]) {
  return self.indexOf(value) === index;
}

export function isNullOrEmpty(value: string | null | undefined): boolean {
  return value === null || value === undefined || value === '';
}

export function isNullOrNaN(value: number | null | undefined): boolean {
  return value === null || value === undefined || isNaN(value);
}

export function isNullOrEmptyArray(value: any[] | null | undefined): boolean {
  return value === null || value === undefined || value.length <= 0;
}

export function dynamicSort(property: string, sortOrder: number) {
  return function (a: any, b: any) {
    let aVal = a[property];
    let bVal = b[property];

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    let result = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    // If one of the values is null but the other is not, < and > both return false, giving odd
    // alignments in some cases
    if (aVal === null && bVal !== null) result = -1;
    if (aVal !== null && bVal === null) result = 1;
    return result * sortOrder;
  };
}

export function dynamicSubfieldSort(
  property: string,
  subProperty: string,
  sortOrder: number
) {
  return function (a: any, b: any) {
    // tslint:disable-next-line:max-line-length
    const result =
      a[property][subProperty] < b[property][subProperty]
        ? -1
        : a[property][subProperty] > b[property][subProperty]
          ? 1
          : 0;
    return result * sortOrder;
  };
}

export function filterDropdownResults(
  elements: any[],
  queryArray: FilterCriteria[]
) {
  return elements.filter((ft) => {
    const checkArray: any[] = [];
    queryArray.forEach((e) => {
      if (e.type === 'include' && getProp(ft, e.key) && e.value) {
        const check = getProp(ft, e.key)
          .toLowerCase()
          .includes(e.value.toLowerCase());
        if (!check) {
          return;
        }
        checkArray.push(check);
      } else if (e.type === 'startsWith' && getProp(ft, e.key) && e.value) {
        const check = getProp(ft, e.key)
          .toLowerCase()
          .startsWith(e.value.toLowerCase());
        if (!check) {
          return;
        }
        checkArray.push(check);
      } else if (e.type === 'array-includes' && e.value) {
        const proplist = e.key.split('.');
        if (proplist.length > 1 && ft[proplist[0]]) {
          const check = ft[proplist[0]]
            .map((p: any) => p[proplist[1]])
            .includes(e.value);
          if (!check) {
            return;
          }
          checkArray.push(check);
        }
      }
    });
    return (
      checkArray.length === queryArray.length &&
      !checkArray.some((e) => e === false)
    );
  });
}

const getProp = (obj: any, prop: string) => {
  return prop.split('.').reduce((r, e) => {
    return r[e];
  }, obj);
};

export function getDirtyValues(form: any) {
  const dirtyValues: any = {};

  Object.keys(form.controls).forEach((key) => {
    const currentControl = form.controls[key];
    if (currentControl.dirty) {
      if (!isNullOrUndefined(currentControl.push)) {
        dirtyValues[key] = [
          ...currentControl.controls.map((c: { value: any }) => c.value),
        ];
      } else if (currentControl.controls) {
        dirtyValues[key] = getDirtyValues(currentControl);
      } else {
        dirtyValues[key] = currentControl.value;
      }
    }
  });

  return dirtyValues;
}

export function clearFormArray(formArray: FormArray) {
  while (formArray.length !== 0) {
    formArray.removeAt(0);
  }
}

export function buildAuditHistory(history: any[]): Audit[] {
  if (!isNullOrEmptyArray(history)) {
    history.forEach((h) => {
      // Handle controller notes returning in a different format from the rest of the app
      if (h.timestamp) {
        h.updatedAt = h.timestamp;
      }

      if (h.user) {
        h.updater = h.user;
      }

      h.updatedAt = getCorrectedDate(h.updatedAt!)!;
    });
    return history;
  } else {
    return history;
  }
}

export function isNullOrEmptyString(object: string): boolean {
  return (
    isNullOrUndefined(object) ||
    (!isNullOrUndefined(object) && object.trim() === '')
  );
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url.startsWith('http') ? url : `https://${url}`);
    return true;
  } catch {
    return false;
  }
}

export function getStartDate(
  isHistorical: boolean,
  shiftDate: Date
): Date | null {
  if (!shiftDate) {
    return null;
  }
  if (isHistorical === true) {
    // Figure out start date based on shiftDate
    let startDate: Date = new Date(
      shiftDate.getFullYear(),
      shiftDate.getMonth(),
      shiftDate.getDate(),
      SHIFT_START_HOUR,
      SHIFT_START_MINUTE
    );
    return startDate;
  } else {
    // Figure out start based on current time
    let now: Date = new Date();
    let shiftStartToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      SHIFT_START_HOUR,
      SHIFT_START_MINUTE
    );
    let shiftEndToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      shiftStartToday.getHours() + SHIFT_LENGTH_IN_HOURS,
      SHIFT_START_MINUTE
    );

    if (now.getTime() < shiftStartToday.getTime()) {
      // Between midnight and 5:45 am - Night Shift
      // startDate is shiftStart yesterday
      let returnDate = shiftStartToday;
      returnDate.setDate(shiftStartToday.getDate() - 1);
      return returnDate;
    } else if (now.getTime() > shiftEndToday.getTime()) {
      // Between 5:45 PM and midnight - Night Shift
      // startDate is shiftStart today
      return shiftStartToday;
    } else {
      // Between 5:45 AM and 5:45 PM - Day Shift
      // startDate is shiftEnd yesterday
      let returnDate = shiftEndToday;
      returnDate.setDate(shiftEndToday.getDate() - 1);
      return returnDate;
    }
  }
}

export function getEndDate(isHistorical: boolean, shiftDate: Date): Date {
  if (isHistorical === true) {
    // Figure out end date based on shiftDate
    let endDate: Date = new Date(
      shiftDate.getFullYear(),
      shiftDate.getMonth(),
      shiftDate.getDate(),
      SHIFT_START_HOUR,
      SHIFT_START_MINUTE
    );
    endDate.setDate(endDate.getDate() + 1);
    return endDate;
  } else {
    // Figure out end based on current time
    let now: Date = new Date();
    let shiftStartToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      SHIFT_START_HOUR,
      SHIFT_START_MINUTE
    );
    let shiftEndToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      shiftStartToday.getHours() + SHIFT_LENGTH_IN_HOURS,
      SHIFT_START_MINUTE
    );

    if (now.getTime() < shiftStartToday.getTime()) {
      // Between midnight and 5:45 am - Night Shift
      // endDate is shiftStart today
      return shiftStartToday;
    } else if (now.getTime() > shiftEndToday.getTime()) {
      // Between 5:45 PM and midnight - Night Shift
      // endDate is shiftStart tomorrow
      let returnDate: Date = shiftStartToday;
      returnDate.setDate(shiftStartToday.getDate() + 1);
      return returnDate;
    } else {
      // Between 5:45 AM and 5:45 PM - Day Shift
      // endDate is shiftEnd today
      return shiftEndToday;
    }
  }
}

export const SHIFT_START_HOUR = 5;
export const SHIFT_START_MINUTE = 45;
export const SHIFT_LENGTH_IN_HOURS = 12;
